import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  Controls,
  type Connection,
  type Edge,
  type EdgeChange,
  type EdgeTypes,
  type IsValidConnection,
  type Node,
  type NodeChange,
  type NodeTypes
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { apiAdminshopsShopIdcategoriesIdPatch } from '@r/api/generated/client.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import type { ProductCategoryProductCategoryReadModel } from '@r/api/generated/client.schemas.ts';
import {
  buildAttachParentPatchPayload,
  buildCategoryLookup,
  invalidateAdminShopCategoriesQueries
} from './custom-shop-categories-api.ts';
import {
  CategoryFlowActionsContext,
  type CategoryFlowActionsContextValue
} from './custom-shop-categories-flow-context.tsx';
import { CategoryFlowEdge } from './custom-shop-categories-flow-edge.tsx';
import { CategoryFlowNode } from './custom-shop-categories-flow-node.tsx';
import {
  type CategoryLinkInvalidReason,
  validateCategoryLink
} from './custom-shop-categories-flow-rules.ts';
import { categoriesToFlowElements } from './custom-shop-categories-flow-utils.ts';

const nodeTypes: NodeTypes = {
  category: CategoryFlowNode
};

const edgeTypes: EdgeTypes = {
  category: CategoryFlowEdge
};

const attachErrorKeys: Record<CategoryLinkInvalidReason, string> = {
  self_link: 'admin.shops.categories.attach.error.self_link',
  parent_not_found: 'admin.shops.categories.attach.error',
  child_not_found: 'admin.shops.categories.attach.error',
  parent_not_root: 'admin.shops.categories.attach.error.parent_not_root',
  child_already_has_parent: 'admin.shops.categories.attach.error.already_has_parent',
  child_has_children: 'admin.shops.categories.attach.error.child_has_children'
};

interface CustomShopCategoriesFlowProps {
  shopId: number;
  categories: readonly ProductCategoryProductCategoryReadModel[];
  canManageCategories?: boolean;
}

/** Canvas React Flow interne : synchronisation, connexions et actions admin. */
function CustomShopCategoriesFlowCanvas({
  shopId,
  categories,
  canManageCategories = false
}: CustomShopCategoriesFlowProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showOrvalErrorToast = useOrvalErrorToast();
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [removingChildId, setRemovingChildId] = useState<number | null>(null);
  const [attachingChildId, setAttachingChildId] = useState<number | null>(null);

  /** Resynchronise le canvas à chaque rechargement des catégories API. */
  useEffect(() => {
    const { nodes: nextNodes, edges: nextEdges } = categoriesToFlowElements(categories);
    setNodes(nextNodes as Node[]);
    setEdges(nextEdges as Edge[]);
  }, [categories]);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => setNodes((snapshot) => applyNodeChanges(changes, snapshot)),
    []
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => setEdges((snapshot) => applyEdgeChanges(changes, snapshot)),
    []
  );

  /** Affiche un toast métier lorsqu'un lien parent-enfant est refusé. */
  const showLinkError = useCallback(
    (reason: CategoryLinkInvalidReason) => {
      toast.error(t(attachErrorKeys[reason]));
    },
    [t]
  );

  /** Autorise uniquement les connexions valides (un niveau, catégories éligibles). */
  const isValidConnection = useCallback<IsValidConnection>(
    (connection) => {
      if (!canManageCategories || !connection.source || !connection.target) {
        return false;
      }

      const validation = validateCategoryLink(
        categories,
        Number(connection.source),
        Number(connection.target)
      );

      return validation.isValid;
    },
    [canManageCategories, categories]
  );

  /** Persiste le lien parent-enfant via PATCH puis rafraîchit la liste des catégories. */
  const onConnect = useCallback(
    async (connection: Connection) => {
      if (!canManageCategories || !connection.source || !connection.target) {
        return;
      }

      const parentId = Number(connection.source);
      const childId = Number(connection.target);
      const validation = validateCategoryLink(categories, parentId, childId);

      if (!validation.isValid) {
        showLinkError(validation.reason);
        return;
      }

      const parentCategory = buildCategoryLookup(categories).get(parentId);
      if (!parentCategory) {
        showLinkError('parent_not_found');
        return;
      }

      setAttachingChildId(childId);

      try {
        await apiAdminshopsShopIdcategoriesIdPatch(
          String(shopId),
          String(childId),
          buildAttachParentPatchPayload(parentId)
        );
        invalidateAdminShopCategoriesQueries(queryClient, shopId);
        toast.success(t('admin.shops.categories.attach.success'));
      } catch (error) {
        showOrvalErrorToast(error, t('admin.shops.categories.attach.error'));
      } finally {
        setAttachingChildId(null);
      }
    },
    [canManageCategories, categories, queryClient, shopId, showLinkError, showOrvalErrorToast, t]
  );

  /** Supprime le lien parent d'une sous-catégorie (PATCH parent: null). */
  const onRemoveParentLink = useCallback(
    async (childCategoryId: number) => {
      setRemovingChildId(childCategoryId);

      try {
        await apiAdminshopsShopIdcategoriesIdPatch(String(shopId), String(childCategoryId), {
          parent: null
        });
        invalidateAdminShopCategoriesQueries(queryClient, shopId);
        toast.success(t('admin.shops.categories.detach.success'));
      } catch (error) {
        showOrvalErrorToast(error, t('admin.shops.categories.detach.error'));
      } finally {
        setRemovingChildId(null);
      }
    },
    [queryClient, shopId, showOrvalErrorToast, t]
  );

  const flowActions = useMemo<CategoryFlowActionsContextValue>(
    () => ({
      canManageCategories,
      removingChildId,
      attachingChildId,
      onRemoveParentLink
    }),
    [attachingChildId, canManageCategories, onRemoveParentLink, removingChildId]
  );

  return (
    <CategoryFlowActionsContext value={flowActions}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultEdgeOptions={{ type: 'category' }}
        nodesConnectable={canManageCategories && attachingChildId === null}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={(connection) => void onConnect(connection)}
        isValidConnection={isValidConnection}
        fitView
      >
        <Background />
        <Controls />
      </ReactFlow>
    </CategoryFlowActionsContext>
  );
}

/** Canvas React Flow alimenté par les catégories de la boutique. */
export function CustomShopCategoriesFlow(props: CustomShopCategoriesFlowProps) {
  return (
    <div className="h-[28rem] w-full rounded-xl border bg-muted/20">
      <ReactFlowProvider>
        <CustomShopCategoriesFlowCanvas {...props} />
      </ReactFlowProvider>
    </div>
  );
}
